import type { ComponentType } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { EmbeddedLayout } from '@/components/layout/EmbeddedLayout';
import { ProjectLayout } from '@/components/layout/ProjectLayout';
import { Dashboard } from '@/pages/Dashboard';
import { Home } from '@/pages/Home';
import { Projects } from '@/pages/Projects';
import { Clients } from '@/pages/Clients';
import { ProjectDetail } from '@/pages/ProjectDetail';
import { ProjectDiscussions } from '@/pages/ProjectDiscussions';
import { TaskListDetail } from '@/pages/TaskListDetail';
import { ProjectTaskLists } from '@/pages/ProjectTaskLists';
import { TaskBoard } from '@/pages/TaskBoard';
import { ProjectFiles } from '@/pages/ProjectFiles';
import { ActivityPage } from '@/pages/Activity';
import { Settings } from '@/pages/Settings';
import { SettingsProfile } from '@/pages/SettingsProfile';
import { SettingsNotifications } from '@/pages/SettingsNotifications';
import { SettingsTeam } from '@/pages/SettingsTeam';
import { SettingsIntegrations } from '@/pages/SettingsIntegrations';
import { SettingsSlackConfig } from '@/pages/SettingsSlackConfig';
import { SettingsClickupConfig } from '@/pages/SettingsClickupConfig';
import { Announcements } from '@/pages/Announcements';
import { SettingsData } from '@/pages/SettingsData';
import { SettingsStatuses } from '@/pages/SettingsStatuses';
import { SettingsApiKeys } from '@/pages/SettingsApiKeys';
import { Help } from '@/pages/Help';
import { ProjectEvents } from '@/pages/ProjectEvents';
import { Tasks } from '@/pages/Tasks';
import { Forms } from '@/pages/Forms';
import { FormsManage } from '@/pages/FormsManage';
import { FormEditor } from '@/pages/FormEditor';
import { FormFill } from '@/pages/FormFill';
import { ProjectForms } from '@/pages/ProjectForms';

export type RouteRole = 'agency-member' | 'agency-admin' | 'agency-owner';

export interface RouteConfig {
  path?: string;
  index?: boolean;
  component?: ComponentType;
  layout?: ComponentType;
  redirect?: string;
  requiredRole?: RouteRole;
  children?: RouteConfig[];
}

export { AppLayout, EmbeddedLayout };

// Single source of truth for all routes and their required permissions.
// Add new routes here — access control is enforced automatically via renderRoutes.
export const ROUTE_CONFIG: RouteConfig[] = [
  {
    layout: AppLayout,
    children: [
      // "/" opens your last project (see Home); the Dashboard answers at /dashboard
      { path: '/', component: Home },
      { path: '/dashboard', component: Dashboard },
      { path: '/organizations', component: Clients },
      { path: '/projects', component: Projects },
      // Direct messages live in the chat applet on the app bar, not in the
      // Hub (Jesse, 2026-10-07); old /messages bookmarks fall through to "/".
      { path: '/activity', component: ActivityPage },
      { path: '/tasks', component: Tasks },
      { path: '/forms', component: Forms },
      { path: '/forms/manage', component: FormsManage, requiredRole: 'agency-member' },
      { path: '/forms/manage/:formId', component: FormEditor, requiredRole: 'agency-member' },
      { path: '/forms/:formId/fill/:responseId', component: FormFill },
      // Announcements is an AUTHORING tool, not a preference — it composes and
      // schedules what clients see. It sat under /settings only because that is
      // where it was first built; a nav item pointing into the settings shell
      // makes a tool feel like a checkbox, so it lives at the top level and
      // /settings/announcements now redirects here.
      { path: '/announcements', component: Announcements, requiredRole: 'agency-admin' },
      { path: '/help', component: Help },

      // Redirects from legacy routes
      { path: '/team', redirect: '/settings/team' },

      // Settings routes
      {
        path: '/settings',
        layout: Settings,
        children: [
          { path: 'profile', component: SettingsProfile },
          { path: 'notifications', component: SettingsNotifications },
          { path: 'team', component: SettingsTeam, requiredRole: 'agency-admin' },
          { path: 'statuses', component: SettingsStatuses, requiredRole: 'agency-admin' },
          { path: 'integrations', component: SettingsIntegrations, requiredRole: 'agency-admin' },
          { path: 'api-keys', component: SettingsApiKeys, requiredRole: 'agency-admin' },
          { path: 'integrations/slack/:installationId', component: SettingsSlackConfig, requiredRole: 'agency-admin' },
          { path: 'integrations/clickup', component: SettingsClickupConfig, requiredRole: 'agency-admin' },
          { path: 'announcements', redirect: '/announcements' },
          { path: 'data', component: SettingsData, requiredRole: 'agency-admin' },
        ],
      },

      // Project routes
      {
        path: '/projects/:projectId',
        layout: ProjectLayout,
        children: [
          { index: true, component: ProjectDetail },
          { path: 'discussions', component: ProjectDiscussions },
          // Thread URLs render the list with the thread open as a takeover modal
          // (the standalone detail page is deprecated).
          { path: 'discussions/:threadId', component: ProjectDiscussions },
          { path: 'custom/:typeSlug', component: ProjectDiscussions },
          { path: 'custom/:typeSlug/:threadId', component: ProjectDiscussions },
          { path: 'tasks', component: ProjectTaskLists },
          { path: 'tasks/:listId', component: TaskListDetail },
          { path: 'board', component: TaskBoard },
          { path: 'files', component: ProjectFiles },
          { path: 'events', component: ProjectEvents },
          { path: 'forms', component: ProjectForms },
        ],
      },
    ],
  },
];
